import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button, Card, Col, DatePicker, Row, Select, Space, Spin, Typography, message, Modal, Table
} from 'antd';
import { FileExcelOutlined, FilePdfOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { getUserPrimaryCompanyId, hasRole } from '../../../../utils/permissionUtils';
import { exportToPdf } from '../../components/ReportExport';
import StatementCustomerBlock from './components/StatementCustomerBlock';

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

const pick = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== '') ?? null;

const StatementReport = () => {
  const { t, currentLanguage } = useLanguage();
  const exportContainerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [monthRange, setMonthRange] = useState([dayjs().startOf('month').subtract(5, 'month'), dayjs().endOf('month')]);
  const [companyFilter, setCompanyFilter] = useState(() => getUserPrimaryCompanyId());
  const [companiesForSelect, setCompaniesForSelect] = useState([]);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerRows, setPickerRows] = useState([]);
  const [pickerKeyword, setPickerKeyword] = useState('');

  const userInfo = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('userInfo') || '{}') : {};
  const isTenantAdmin = hasRole(userInfo, 'Tenant_Admin');

  const fetchWithAuth = useCallback((url, options = {}) => {
    const token = localStorage.getItem('token');
    const headers = { ...(options.headers || {}), 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    headers['X-UI-Locale'] = currentLanguage || 'zh-TC';
    return fetch(url, { ...options, headers });
  }, [currentLanguage]);

  useEffect(() => {
    if (!isTenantAdmin) return;
    fetchWithAuth('/api/commerce/companies-for-select')
      .then((r) => r.ok ? r.json() : [])
      .then((list) => setCompaniesForSelect(Array.isArray(list) ? list : []))
      .catch(() => {});
  }, [fetchWithAuth, isTenantAdmin]);

  const loadPickerCustomers = useCallback(async (kw = '') => {
    setPickerLoading(true);
    try {
      const params = new URLSearchParams({ page: '1', pageSize: '100' });
      if (kw.trim()) params.set('search', kw.trim());
      if (isTenantAdmin && companyFilter) params.set('companyId', companyFilter);
      const res = await fetchWithAuth(`/api/commerce/customers-for-select?${params.toString()}`);
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setPickerRows(Array.isArray(json?.items) ? json.items : Array.isArray(json) ? json : []);
    } catch (err) {
      message.error(err.message);
    } finally {
      setPickerLoading(false);
    }
  }, [fetchWithAuth, companyFilter, isTenantAdmin]);

  const loadReport = useCallback(async () => {
    if (!monthRange || !monthRange[0] || !monthRange[1]) {
      message.warning(t('reports.commerce.monthRange') || '請選擇月份範圍');
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('dateFrom', monthRange[0].startOf('month').format('YYYY-MM-DD'));
      params.set('dateTo', monthRange[1].endOf('month').format('YYYY-MM-DD'));
      if (isTenantAdmin && companyFilter) params.set('companyId', companyFilter);
      selectedCustomerIds.forEach((id) => params.append('customerIds', id));
      const res = await fetchWithAuth(`/api/reports/commerce/statement?${params.toString()}`);
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setData(json);
    } catch (err) {
      message.error(`${t('reports.commerce.loadFailed')}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [monthRange, companyFilter, selectedCustomerIds, fetchWithAuth, isTenantAdmin, t]);

  const flatExportRows = useMemo(() => {
    if (!data?.customers) return [];
    const rows = [];
    data.customers.forEach((cust) => {
      (cust.months || []).forEach((month) => {
        (month.invoices || []).forEach((inv) => {
          rows.push({
            customerNo: cust.customerNo,
            customerName: cust.customerName,
            paymentTerm: inv.paymentTerm || cust.paymentTerm,
            month: month.monthLabel,
            orderNo: inv.orderNo,
            deliveryDate: inv.deliveryDate ? String(inv.deliveryDate).slice(0, 10) : '',
            amount: inv.totalAmount,
            currency: inv.currency,
            status: inv.status,
            lineCount: inv.lineCount
          });
        });
      });
    });
    return rows;
  }, [data]);

  const handleExportExcel = () => {
    try {
      const headers = [
        t('commerceCustomers.customerNo'),
        t('commerceCustomers.customerNameCol'),
        t('reports.commerce.paymentTerm'),
        t('reports.commerce.month'),
        t('reports.commerce.orderNo'),
        t('reports.commerce.deliveryDate'),
        t('reports.commerce.amount'),
        t('commerceSalesOrders.currency'),
        t('commerceSalesOrders.status'),
        t('reports.commerce.lineCount')
      ];
      const aoa = [
        headers,
        ...flatExportRows.map((r) => [
          r.customerNo, r.customerName, r.paymentTerm, r.month,
          r.orderNo, r.deliveryDate, r.amount, r.currency, r.status, r.lineCount
        ])
      ];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Statement');
      XLSX.writeFile(wb, `commerce_statement_${dayjs().format('YYYY-MM-DD')}.xlsx`);
      message.success(t('reports.commerce.exportSuccess'));
    } catch (err) {
      message.error(`${t('reports.commerce.exportFailed')}: ${err.message}`);
    }
  };

  const handleExportPdf = async () => {
    const columns = [
      { title: t('commerceCustomers.customerNo'), dataIndex: 'customerNo' },
      { title: t('commerceCustomers.customerNameCol'), dataIndex: 'customerName' },
      { title: t('reports.commerce.orderNo'), dataIndex: 'orderNo' },
      { title: t('reports.commerce.deliveryDate'), dataIndex: 'deliveryDate' },
      { title: t('reports.commerce.amount'), dataIndex: 'amount' },
      { title: t('commerceSalesOrders.status'), dataIndex: 'status' }
    ];
    try {
      await exportToPdf(flatExportRows, columns, 'commerce_statement', {
        title: t('reports.commerce.statement'),
        statistics: data ? { [t('reports.commerce.grandTotal')]: data.grandTotal } : null
      });
      message.success(t('reports.commerce.exportSuccess'));
    } catch (err) {
      message.error(`${t('reports.commerce.exportFailed')}: ${err.message}`);
    }
  };

  const customerLabel = selectedCustomerIds.length === 0
    ? (t('reports.commerce.allCustomers') || '全部客戶')
    : `${selectedCustomerIds.length} ${t('reports.commerce.customers') || '客戶'}`;

  return (
    <div>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 12]} align="middle">
          {isTenantAdmin && (
            <Col span={6}>
              <Text strong>{t('reports.commerce.company')}</Text>
              <Select
                allowClear
                style={{ width: '100%', marginTop: 4 }}
                placeholder={t('reports.commerce.selectCompany')}
                value={companyFilter}
                onChange={setCompanyFilter}
                options={companiesForSelect.map((c) => ({ label: c.name, value: c.id }))}
              />
            </Col>
          )}
          <Col span={8}>
            <Text strong>{t('reports.commerce.monthRange')}</Text>
            <RangePicker
              picker="month"
              style={{ width: '100%', marginTop: 4 }}
              value={monthRange}
              onChange={setMonthRange}
            />
          </Col>
          <Col span={8}>
            <Text strong>{t('reports.commerce.customers')}</Text>
            <Space.Compact style={{ width: '100%', marginTop: 4 }}>
              <Button block onClick={() => { setCustomerPickerOpen(true); loadPickerCustomers(); }}>
                {customerLabel}
              </Button>
              {selectedCustomerIds.length > 0 && (
                <Button onClick={() => setSelectedCustomerIds([])}>{t('commerceSalesOrders.clearCustomer')}</Button>
              )}
            </Space.Compact>
          </Col>
          <Col span={24}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={loadReport} loading={loading}>
                {t('common.search') || '查詢'}
              </Button>
              <Button icon={<ReloadOutlined />} onClick={loadReport} disabled={loading} />
              <Button icon={<FileExcelOutlined />} onClick={handleExportExcel} disabled={!flatExportRows.length}>
                {t('reports.commerce.exportExcel')}
              </Button>
              <Button icon={<FilePdfOutlined />} onClick={handleExportPdf} disabled={!flatExportRows.length}>
                {t('reports.commerce.exportPdf')}
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Spin spinning={loading}>
        {data && (
          <>
            <Title level={5} style={{ marginBottom: 16 }}>
              {t('reports.commerce.grandTotal')}: HKD {(Number(data.grandTotal) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </Title>
            <div ref={exportContainerRef}>
              {(data.customers || []).length === 0 ? (
                <Card><Text type="secondary">{t('reports.commerce.noData')}</Text></Card>
              ) : (
                data.customers.map((cust) => (
                  <StatementCustomerBlock key={cust.customerId || cust.customerNo} customer={cust} />
                ))
              )}
            </div>
          </>
        )}
      </Spin>

      <Modal
        title={t('reports.commerce.selectCustomers')}
        open={customerPickerOpen}
        onCancel={() => setCustomerPickerOpen(false)}
        width={900}
        footer={null}
      >
        <Space style={{ marginBottom: 12 }}>
          <Button onClick={() => loadPickerCustomers(pickerKeyword)} icon={<SearchOutlined />}>
            {t('common.search')}
          </Button>
          <Button
            type="primary"
            onClick={() => {
              setCustomerPickerOpen(false);
              loadReport();
            }}
          >
            {t('common.confirm') || '確認'}
          </Button>
        </Space>
        <Table
          rowKey={(r) => String(pick(r.id, r.Id))}
          loading={pickerLoading}
          size="small"
          dataSource={pickerRows}
          rowSelection={{
            selectedRowKeys: selectedCustomerIds,
            onChange: (keys) => setSelectedCustomerIds(keys.map(String))
          }}
          columns={[
            { title: t('commerceCustomers.customerNo'), dataIndex: 'customerNo', key: 'customerNo' },
            { title: t('commerceCustomers.branchNo'), dataIndex: 'branchNo', key: 'branchNo', width: 80 },
            { title: t('commerceCustomers.customerNameCol'), dataIndex: 'displayName', key: 'displayName' }
          ]}
          pagination={{ pageSize: 10 }}
        />
      </Modal>
    </div>
  );
};

export default StatementReport;
